import { FC, useCallback, useMemo } from 'react'
import { captureException } from '@sentry/react'

import { Text } from '@revolut-internal/ui-kit'

import { DropdownSelect } from 'components/Filters/DropdownSelect'
import { SingleOption } from 'components/Filters/types'
import { useChannel } from 'hooks/useChannel'
import { useQueueStatsFilterQuery } from 'modules/api/analytics/queries'
import { humanize } from 'utils/format'

import { aggregationDimensionOptionsMap } from '../consts'
import { AggregationDimensionValue } from '../TicketsVolumeDetails'

import { calculateCountByOptions, CountByOptionValue } from './calculateCountByOptions'

type CountBySelectProps = {
  dimension: AggregationDimensionValue
  onChange: (arg: AggregationDimensionValue) => void
}

const parseValue = (value?: string | null) => {
  if (!value) {
    return undefined
  }

  try {
    const parsedValue = JSON.parse(value) as CountByOptionValue

    if (!parsedValue || !('value' in parsedValue)) {
      return undefined
    }

    return parsedValue
  } catch (e) {
    captureException(e)
    return undefined
  }
}

export const CountBySelect: FC<CountBySelectProps> = ({ dimension, onChange }) => {
  const { channel } = useChannel()
  const { data: additionalFilters, isPending } = useQueueStatsFilterQuery({
    channel,
  })
  const preparedValue = JSON.stringify(
    dimension.dimensionTarget
      ? {
          value: dimension.dimensionTarget,
          parentKey: dimension.dimension,
        }
      : { value: dimension.dimension },
  )

  const options = useMemo(() => {
    return calculateCountByOptions(channel, additionalFilters?.items)
  }, [additionalFilters?.items, channel])

  const renderOption = useCallback((option: SingleOption<string>) => {
    const parsedValue = option.value && parseValue(option.value)

    if (!parsedValue) {
      return option.label
    }

    return <Text pl={parsedValue?.parentKey ? 's-16' : undefined}>{option.label}</Text>
  }, [])

  const handleChange = useCallback(
    (newValue: string | null) => {
      const parsedValue = parseValue(newValue)

      if (!parsedValue) {
        return
      }

      // `parentKey` could be anything comes from BE, so we can't create a config for them
      const dimensionLabel = parsedValue.parentKey
        ? humanize(parsedValue.parentKey, true)
        : (aggregationDimensionOptionsMap[channel].find(
            configItem => configItem.value === parsedValue.value,
          )?.label ?? humanize(parsedValue.value, true))

      parsedValue.parentKey
        ? onChange({
            dimension: parsedValue.parentKey,
            dimensionTarget: parsedValue.value,
            dimensionLabel,
          })
        : onChange({ dimension: parsedValue.value, dimensionLabel })
    },
    [onChange, channel],
  )

  if (!channel) {
    return null
  }

  return (
    <DropdownSelect
      label="Count by"
      value={preparedValue}
      loadingState={isPending ? 'pending' : 'ready'}
      options={options}
      maxHeight={305}
      onChange={handleChange}
      renderOption={renderOption}
    />
  )
}
